<script setup lang="ts">

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppTable, AppTd, AppTh, AppTr } from '@shared/ui/tables'
import { AppIcon } from '@shared/ui/icons'

import ChangelogCursorPagination from './ChangelogCursorPagination.vue'
import { useTableChangelog } from '../model/table-changelog.hook'

defineOptions({
  name: 'TableChangelog',
})

const {
  actions,
  isLoading,
  hasActions,
  hasNext,
  hasPrev,
  perPage,
  count,
  expandedActionId,
  items,
  isItemsLoading,
  formatDateTime,
  getActionLabel,
  getItemChange,
  toggleItems,
  setChangePerPage,
  goNext,
  goPrev,
} = useTableChangelog()
</script>

<template>
  <div class="table-changelog">
    <AppTable
      class="rounded-table"
      :loading="isLoading"
      :paginated="false"
    >
      <template #head>
        <AppTr>
          <AppTh class="text-center table-changelog__id-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              #
            </AppText>
          </AppTh>
          <AppTh class="table-changelog__date-cell">
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Date &amp; time
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Action
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              Context
            </AppText>
          </AppTh>
          <AppTh>
            <AppText
              name="body1-medium"
              color="slate-900"
            >
              User
            </AppText>
          </AppTh>
          <AppTh class="table-changelog__toggle-cell" />
        </AppTr>
      </template>

      <template
        v-if="hasActions"
        #body
      >
        <template
          v-for="row in actions"
          :key="row.id"
        >
          <AppTr>
            <AppTd class="medium text-center table-changelog__id-cell">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ row.id }}
              </AppText>
            </AppTd>
            <AppTd class="medium table-changelog__date-cell">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ formatDateTime(row.created_at) }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ getActionLabel(row) }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppText
                name="body1"
                color="slate-900"
              >
                {{ row.context ?? '—' }}
              </AppText>
            </AppTd>
            <AppTd class="medium">
              <AppText
                name="body1"
                color="slate-900"
              >
                <template v-if="row.user_display_type">
                  {{ row.user_display_type }} #{{ row.user_id }} ({{ row.user_name }})
                </template>
                <template v-else>
                  —
                </template>
              </AppText>
            </AppTd>
            <AppTd class="zero-padding table-changelog__toggle-cell">
              <button
                type="button"
                class="table-changelog__toggle"
                :class="{ 'table-changelog__toggle--open': expandedActionId === row.id }"
                aria-label="Toggle details"
                @click="toggleItems(row)"
              >
                <AppIcon
                  :icon="faChevronDown"
                  size="small"
                />
              </button>
            </AppTd>
          </AppTr>

          <AppTr v-if="expandedActionId === row.id">
            <AppTd
              class="zero-padding table-changelog__details"
              :colspan="6"
            >
              <div
                v-if="isItemsLoading"
                class="table-changelog__details-loader"
              >
                <AppText
                  name="body2"
                  color="slate-500"
                >
                  Loading…
                </AppText>
              </div>

              <AppText
                v-else-if="!items.length"
                name="body2"
                color="slate-500"
                class="table-changelog__details-empty"
              >
                No changes recorded for this action.
              </AppText>

              <AppTable
                v-else
                :paginated="false"
              >
                <template #head>
                  <AppTr>
                    <AppTh>
                      <AppText
                        name="body2-medium"
                        color="slate-900"
                      >
                        Date &amp; time
                      </AppText>
                    </AppTh>
                    <AppTh>
                      <AppText
                        name="body2-medium"
                        color="slate-900"
                      >
                        Instance
                      </AppText>
                    </AppTh>
                    <AppTh>
                      <AppText
                        name="body2-medium"
                        color="slate-900"
                      >
                        Change
                      </AppText>
                    </AppTh>
                    <AppTh>
                      <AppText
                        name="body2-medium"
                        color="slate-900"
                      >
                        Old value
                      </AppText>
                    </AppTh>
                    <AppTh>
                      <AppText
                        name="body2-medium"
                        color="slate-900"
                      >
                        New value
                      </AppText>
                    </AppTh>
                  </AppTr>
                </template>
                <template #body>
                  <AppTr
                    v-for="item in items"
                    :key="item.id"
                  >
                    <AppTd class="medium">
                      <AppText
                        name="body2"
                        color="slate-900"
                      >
                        {{ formatDateTime(item.created_at) }}
                      </AppText>
                    </AppTd>
                    <AppTd class="medium">
                      <AppText
                        name="body2"
                        color="slate-900"
                      >
                        {{ item.instance_type }} #{{ item.instance_id }}
                      </AppText>
                    </AppTd>
                    <AppTd class="medium">
                      <AppText
                        name="body2"
                        color="slate-900"
                      >
                        {{ getItemChange(item) }}
                      </AppText>
                    </AppTd>
                    <AppTd class="medium">
                      <AppText
                        name="body2"
                        color="slate-500"
                      >
                        {{ item.old_value ?? '—' }}
                      </AppText>
                    </AppTd>
                    <AppTd class="medium">
                      <AppText
                        name="body2"
                        color="slate-900"
                      >
                        {{ item.value ?? '—' }}
                      </AppText>
                    </AppTd>
                  </AppTr>
                </template>
              </AppTable>
            </AppTd>
          </AppTr>
        </template>
      </template>
    </AppTable>

    <ChangelogCursorPagination
      :per-page="perPage"
      :count="count"
      :has-prev="hasPrev"
      :has-next="hasNext"
      :disabled="isLoading"
      @change:per-page="setChangePerPage"
      @prev="goPrev"
      @next="goNext"
    />
  </div>
</template>

<style scoped lang="scss" src="./TableChangelog.scss" />
